import { ColumnDef, Table } from "@tanstack/react-table";
import { Badge } from "@/components/ui/badge";
import { OPERATOR_PHRASE, type Alarm } from "./types";
import { ActionsMenuColumn } from "@/components/tableColumns/ActionsMenuColumn";
import { toJS } from "mobx";
import { CUSTOM_FILTERS } from "@/config/list";
import { AlarmActiveSwitch } from "./components/AlarmActiveSwitch";

interface ColumnOptions {
	onAlarmToggled: () => void | Promise<void>;
	onEdit: (alarm: Alarm) => void;
	onDelete: (alarm: Alarm) => void;
	onViewLog: (alarm: Alarm) => void;
	onUnsubscribe: (alarm: Alarm) => void;
	canManageMembers: boolean;
}

const ConditionBadges = ({ alarm }: { alarm: Alarm }) => {
	const unit = alarm.parameter.unit.symbol ? ` ${alarm.parameter.unit.symbol}` : "";
	const operator = OPERATOR_PHRASE[alarm.operator];

	return (
		<div className="flex flex-wrap items-center gap-1">
			<Badge
				variant="outline"
				className="border-blue-200 bg-blue-50 text-blue-700 dark:border-blue-500/30 dark:bg-blue-500/10 dark:text-blue-300"
			>
				{alarm.parameter.name}
			</Badge>
			{alarm.warningThreshold != null && (
				<Badge
					variant="outline"
					className="gap-1 border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-300"
				>
					<span className="font-semibold">Warning</span>
					<span className="font-normal">
						{operator} {alarm.warningThreshold}
						{unit}
					</span>
				</Badge>
			)}
			<Badge
				variant="outline"
				className="gap-1 border-red-200 bg-red-50 text-red-700 dark:border-red-500/30 dark:bg-red-500/10 dark:text-red-300"
			>
				<span className="font-semibold">Critical</span>
				<span className="font-normal">
					{operator} {alarm.criticalThreshold}
					{unit}
				</span>
			</Badge>
		</div>
	);
};

export const getAlarmsColumns = ({
	onAlarmToggled,
	onEdit,
	onDelete,
	onViewLog,
	onUnsubscribe,
	canManageMembers,
}: ColumnOptions): ColumnDef<Alarm>[] => [
	{
		accessorKey: "name",
		header: "Name",
		cell: ({ row }) => <span className="font-medium">{row.original.name}</span>,
	},
	{
		id: "condition",
		header: "Condition",
		cell: ({ row }) => <ConditionBadges alarm={row.original} />,
	},
	{
		id: "structures",
		header: "Structures",
		cell: ({ row }) => {
			const { counts, structureScope } = row.original;
			return (
				<span className="text-sm flex items-center gap-2">
					{structureScope === "all" ? `All structures` : `${counts.structures} structures`}
					{counts.triggered > 0 && <Badge variant="destructive">{counts.triggered} in alarm</Badge>}
				</span>
			);
		},
	},
	{
		id: "active",
		header: "Active",
		cell: ({ row }) => (
			<AlarmActiveSwitch alarm={row.original} canToggle={canManageMembers} onToggled={onAlarmToggled} />
		),
	},
	ActionsMenuColumn<Alarm>({
		actions: [
			{
				label: "View details",
				onSelect: row => {
					const alarm = row.original;
					onViewLog(alarm);
				},
			},
			{
				label: "Unsubscribe",
				onHandleHidden: (_, table) => {
					const isOwnAlarmSelected = isOwnAlarmFilterSelected(table);
					return !isOwnAlarmSelected;
				},
				onSelect: row => {
					const alarm = row.original;
					onUnsubscribe(alarm);
				},
			},
			{
				label: "Edit",
				onHandleHidden: () => {
					return !canManageMembers;
				},
				onSelect: row => {
					const alarm = row.original;
					onEdit(alarm);
				},
			},
			{
				label: "Delete",
				onHandleHidden: () => {
					return !canManageMembers;
				},
				onSelect: row => {
					const alarm = row.original;
					onDelete(alarm);
				},
			},
		],
		header: "",
		className: "w-10",
	}),
];

const isOwnAlarmFilterSelected = (table: Table<Alarm>): boolean => {
	const filters = toJS(table.getState().columnFilters);
	const ownAlarmFilter = filters.find(({ id }) => id.includes(CUSTOM_FILTERS.ownAlarms));
	return ownAlarmFilter?.id ? true : false;
};
